<template>
  <div id="om-calculator" v-observe-visibility="visibilityChanged" v-on:click.once="userTouched = true">
    <div class="container">
      <div class="row justify-content-center">
          <div class="col-lg-3 col-md-6 calc__col">
            <div class="calc__col__title calc__col__title--orange">STRATEGY</div>
            <div class="row">
              <div class="col-6">
                <h5 class="slider__title text-yellow">Selling Price<br/> ({{ pricing.value | currency }})</h5>
              </div>
              <div class="col-6">
                <h5 class="slider__title text-orange">Marketing Budget<br/> ({{ (pricing.value - 150 ) * marketingBudget.value / 100 | currency }})</h5>
              </div>
            </div>
            <div class="row text-center" style="position: relative;">
              <div class="priceBox col-6">
                  <div class="avgBox">
                    <div class="avgBox__label">Average market price</div>
                  </div>
                  <div class="marginBox" :style="{'height': costPriceHeight +'%'}">
                    <div class="marginBox__budget" :style="{'height': marketingBudget.value +'%'}"></div>
                  </div>
                  <div class="marginalBox">
                    <div class="marginalBox__label">Marginal costs (€ 150)</div>
                  </div>
              </div>
              <div class="col-6">
                <vue-slider v-model="pricing.value" v-bind="pricing.options">
                  <span class="vue-slider-tooltip tooltip-yellow" slot="tooltip" slot-scope="tooltip">
                    {{ tooltip.value | currency }}
                  </span>
                </vue-slider>
              </div>
              <div class="col-6">
                <vue-slider v-model="marketingBudget.value" v-bind="marketingBudget.options">
                  <span class="vue-slider-tooltip tooltip-orange" slot="tooltip" slot-scope="tooltip">
                    {{ tooltip.value | toPercent }}
                  </span>
                </vue-slider>
              </div>
            </div>
            <br />
            <div class="row">
              <div class="col">
                <vue-slider v-model="priceElasticity.value" v-bind="priceElasticity.options">
                  <span class="vue-slider-tooltip tooltip-carrot" slot="tooltip" slot-scope="tooltip">
                    {{ tooltip.value | toPercent }}
                  </span>
                </vue-slider>
                <div class="slider__level slider__level--left-bottom">Low</div>
                <div class="slider__level slider__level--right-bottom">High</div>
                <h5 class="slider__title slider__title--center slider__title--bottom text-carrot"><span>Price Elasticity</span></h5>
              </div>
            </div>
            <br /> <br />
          </div>
          <div class="col-lg-9 col-md-12">
              <div class="row">
                <div class="col-md-6 calc__col">
                  <div class="calc__col__title calc__col__title--green">MARKETING KPIs</div>
                  <div class="column-chart">
                    <div class="chart chart--horizontal clearfix">
                        <div class="item">
                            <div class="bar">
                                <span class="percent">{{ conversionRateCalc | toFixed | toPercent }}</span>
                                <span class="title">Conversion rate</span>
                                <div class="item-progress" :style="{'height': conversionRateHeight +'%'}"></div>
                            </div><!-- //.bar -->
                        </div><!-- //.item -->
                        <div class="item">
                            <div class="bar">
                                <span class="percent">{{ cpcCalc | toFixed | currency }}</span>
                                <span class="title">Max Cost-per-Click</span>
                                <div class="item-progress" :style="{'height': cpcHeight +'%'}"></div>
                            </div><!-- //.bar -->
                        </div><!-- //.item -->
                        <div class="item">
                            <div class="bar">
                                <span class="percent">{{ impressionCalc }}</span>
                                <span class="title">Impressions</span>
                                <div class="item-progress" :style="{'height': impressionHeight +'%'}"></div>
                            </div><!-- //.bar -->
                        </div><!-- //.item -->
                        <div class="item">
                            <div class="bar">
                                <span class="percent">{{ ctrCalc | toPercent }}</span>
                                <span class="title">Ad<br/> Click-through-Rate</span>
                                <div class="item-progress" :style="{'height': ctrHeight +'%'}"></div>
                            </div><!-- //.bar -->
                        </div><!-- //.item -->
                        <div class="item">
                            <div class="bar">
                                <span class="percent">{{ clicksCalc }}</span>
                                <span class="title">Clicks</span>
                                <div class="item-progress" :style="{'height': clickHeight +'%'}"></div>
                            </div><!-- //.bar -->
                        </div><!-- //.item -->
                    </div> <!-- //.chart -->
                  </div> <!-- //.column-chart -->
                </div>
                <div class="col-md-6 calc__col">
                  <div class="calc__col__title calc__col__title--blue">PERFORMANCE</div>
                  <div class="column-chart column-chart--results">
                    <div class="chart chart--vertical clearfix">
                      <div class="item">
                            <div class="bar">
                                <span class="percent">{{ turnoverCalc | currency }}</span>
                                <span class="title">Turnover</span>
                                <div class="item-progress" :style="{'width': turnoverHeight +'%'}"></div>
                            </div><!-- //.bar -->
                        </div><!-- //.item -->
                        <div class="item">
                            <div class="bar">
                                <span class="percent">{{ profitCalc | currency }}</span>
                                <span class="title">Profit</span>
                                <div class="item-progress" :style="{'width': profitHeight +'%'}"></div>
                            </div><!-- //.bar -->
                        </div><!-- //.item -->
                        <div class="item">
                            <div class="bar">
                                <span class="percent">{{ marketingSpendCalc | currency }}</span>
                                <span class="title">Marketing Spend</span>
                                <div class="item-progress" :style="{'width': marketingSpendHeight +'%'}"></div>
                            </div><!-- //.bar -->
                        </div><!-- //.item -->
                        <div class="item">
                            <div class="bar">
                                <span class="percent">{{ netProfitCalc | currency }}</span>
                                <span class="title">Net Profit</span>
                                <div class="item-progress" :style="{'width': netProfitHeight +'%'}"></div>
                            </div><!-- //.bar -->
                        </div><!-- //.item -->
                    </div> <!-- //.chart -->
                  </div> <!-- //.column-chart -->
                </div>
              </div>
          </div> <!-- //.col-md-6 -->
        </div>
    </div>
  </div>
</template>

<script>
import vueSlider from 'vue-slider-component'
import _debounce from 'lodash/debounce'

export default {
  name: 'om-calculator',
  data () {
    return {
      inViewport: false,
      userTouched: false,
      costPrice: {
        min: 150,
        max: 200
      },
      conversionRate: {
        min: 0.1,
        max: 7
      },
      cpcValue: {
        min: 0,
        max: 1
      },
      impression: {
        min: 0,
        max: 20000
      },
      ctrValue: {
        min: 1,
        max: 8
      },
      clicks: {
        min: 0,
        max: 800
      },
      pricing: {
        value: 165,
        options: {
          direction: 'vertical',
          eventType: 'auto',
          width: 6,
          height: 235,
          dotSize: 16,
          min: 150,
          max: 200,
          interval: 1,
          show: true,
          speed: 0.5,
          tooltip: 'hover',
          tooltipDir: 'left',
          lazy: false,
          sliderStyle: [],
          processStyle: {
            'backgroundColor': '#f1c40f'
          },
          piecewiseActiveStyle: null,
          piecewiseStyle: null,
          tooltipStyle: {
            'backgroundColor': '#f1c40f',
            'borderColor': '#f1c40f'
          }
        }
      },
      marketingBudget: {
        value: 50,
        options: {
          direction: 'vertical',
          eventType: 'auto',
          width: 6,
          height: 235,
          dotSize: 16,
          min: 30,
          max: 100,
          interval: 1,
          show: true,
          speed: 0.5,
          tooltip: 'hover',
          tooltipDir: 'right',
          lazy: false,
          sliderStyle: [],
          processStyle: {
            'backgroundColor': '#f39c12'
          },
          piecewiseActiveStyle: null,
          piecewiseStyle: null,
          tooltipStyle: {
            'backgroundColor': '#f39c12',
            'borderColor': '#f39c12'
          }
        }
      },
      priceElasticity: {
        value: 50,
        options: {
          direction: 'horizontal',
          eventType: 'auto',
          width: 'auto',
          height: 6,
          dotSize: 16,
          min: 30,
          max: 100,
          interval: 1,
          show: true,
          speed: 0.5,
          tooltip: false, // 'hover'
          tooltipDir: 'top',
          tooltipStyle: {
            'backgroundColor': '#e67e22',
            'borderColor': '#e67e22'
          },
          lazy: false,
          sliderStyle: [],
          processStyle: {
            'backgroundColor': '#e67e22'
          },
          piecewiseActiveStyle: null,
          piecewiseStyle: null
        }
      }
    }
  },
  computed: {
    marketingBudgetCalc: function () {
      return (this.pricing.value - this.costPrice.min) * this.marketingBudget.value / 100
    },
    conversionRateCalc: function () {
      if ((this.pricing.value - 175) < 0) {
        return (0.025 - (0.045 * ((this.pricing.value - 175) / 25)) * (this.priceElasticity.value / 100)) * 100
      } else {
        return (0.025 - (0.024 * ((this.pricing.value - 175) / 25)) * (this.priceElasticity.value / 100)) * 100
      }
    },
    cpcCalc: function () {
      return Math.min((this.conversionRateCalc * this.marketingBudgetCalc / 100).toFixed(3), 1)
    },
    impressionCalc: function () {
      return (this.cpcCalc / this.cpcValue.max * this.impression.max).toFixed(0)
    },
    ctrCalc: function () {
      if ((this.pricing.value - 175) < 0) {
        return ((0.03 - (0.05 * ((this.pricing.value - 175) / 25)) * (this.priceElasticity.value / 100)) * 100).toFixed(2)
      } else {
        return ((0.03 - (0.02 * ((this.pricing.value - 175) / 25)) * (this.priceElasticity.value / 100)) * 100).toFixed(2)
      }
    },
    clicksCalc: function () {
      return (this.ctrCalc * this.impressionCalc / 100).toFixed(0)
    },
    turnoverCalc: function () {
      return (this.clicksCalc * (this.conversionRateCalc / 100) * this.pricing.value).toFixed(2)
    },
    profitCalc: function () {
      return (this.clicksCalc * (this.conversionRateCalc / 100) * (this.pricing.value - this.costPrice.min)).toFixed(2)
    },
    marketingSpendCalc: function () {
      return (this.clicksCalc * this.cpcCalc).toFixed(2)
    },
    netProfitCalc: function () {
      return (this.profitCalc - this.marketingSpendCalc).toFixed(2)
    },
    conversionRateHeight: function () {
      return (this.conversionRateCalc - this.conversionRate.min) * 100 / (this.conversionRate.max - this.conversionRate.min)
    },
    cpcHeight: function () {
      return (this.cpcCalc - this.cpcValue.min) * 100 / (this.cpcValue.max - this.cpcValue.min)
    },
    impressionHeight: function () {
      return (this.impressionCalc - this.impression.min) * 100 / (this.impression.max - this.impression.min)
    },
    ctrHeight: function () {
      return (this.ctrCalc - this.ctrValue.min) * 100 / (this.ctrValue.max - this.ctrValue.min)
    },
    clickHeight: function () {
      return (this.clicksCalc - this.clicks.min) * 100 / (this.clicks.max - this.clicks.min)
    },
    costPriceHeight: function () {
      return (this.pricing.value - this.costPrice.min) * 100 / (this.costPrice.max - this.costPrice.min)
    },
    turnoverHeight: function () {
      return this.turnoverCalc * 100 / 5200
    },
    profitHeight: function () {
      return this.profitCalc * 100 / 420
    },
    marketingSpendHeight: function () {
      return this.marketingSpendCalc * 100 / 420
    },
    netProfitHeight: function () {
      return this.netProfitCalc * 100 / 105
    }
  },
  filters: {
    currency: function (value) {
      if (!value) return ''
      value = '€ ' + value
      return value
    },
    toPercent: function (value) {
      if (!value) return ''
      value += '%'
      return value
    },
    toFixed: function (value) {
      if (!value) return ''
      return value.toFixed(2)
    }
  },
  methods: {
    visibilityChanged (isVisible, entry) {
      this.inViewport = isVisible
    },
    handleScroll: () => {}
  },
  components: {
    vueSlider
  },
  created () {
    this.handleScroll = _debounce((e) => {
      if (this.inViewport && !this.userTouched) {
        this.pricing.value = Math.floor(Math.random() * (180 - 165)) + 165
      }
    }, 50)

    window.addEventListener('scroll', this.handleScroll)
  },
  destroyed () {
    window.removeEventListener('scroll', this.handleScroll)
  }
}
</script>

<style lang="scss">
$blue: #039be5;
$green: lighten( #4db6ac, 10% );
$yellow: #f1c40f;
$orange: #f39c12;
$pupkin: #d35400;
$carrot: #e67e22;

.text-yellow {
  color: $yellow;
}
.text-orange {
  color: $orange;
}
.text-carrot {
  color: $carrot;
}
#om-calculator {
  margin-top: 15px;
  margin-bottom: 15px;
}
.calc {
  &__col {
    margin-bottom: 30px;
    &__title {
      display: block;
      margin: 0 0 15px;
      padding: 5px 0;
      font-size: 18px;
      font-weight: bold;
      text-align: center;

      &--orange {
        color: #EBB558;
      }
      &--green {
        color: $green;
      }
      &--blue {
        color: $blue;
      }
    }
  }
}
.slider {
  &__title {
    font-size: 15px;
    font-weight: 400;
    text-align: center;
    white-space: nowrap;

    small {
      font-size: 80%;
    }

    &--left {
      left: -10px;
      //text-align: right;
    }
    &--right {
      right: -10px;
      //text-align: left;
    }
    &--center {
      left: 10%;
      right: 10%;
      width: auto;
    }
    &--bottom {
      position: absolute;
      top: 100%;
      transform: translateY(0);
    }
  }
  &__level {
    position: absolute;
    width: 50%;
    font-size: 12px;
    font-weight: 400;
    opacity: .5;

    &--up {
      top: 0;
    }
    &--down {
      bottom: 0;
    }
    &--left {
      left: -10px;
      text-align: right;
    }
    &--right {
      right: -10px;
      text-align: left;
    }
    &--left-bottom {
      left: 20px;
      top: 100%;
      text-align: left;
    }
    &--right-bottom {
      right: 20px;
      top: 100%;
      text-align: right;
    }
  }
}

.vue-slider-component {
  z-index: 2;
  &.vue-slider-vertical {
    width: 22px;
    margin: 0 auto;
  }
  .vue-slider {
  }
  .vue-slider-tooltip {
    &.tooltip-yellow {
      background-color: $yellow;
      border-color: $yellow;
    }
    &.tooltip-orange {
      background-color: $orange;
      border-color: $orange;
    }
    &.tooltip-carrot {
      background-color: $carrot;
      border-color: $carrot;
    }
  }
}
.column-chart {
  position: relative;
  z-index: 20;
  bottom: 0;
  width: 100%;
  height: 250px;
  margin-top: 20px;

  @media (min-width: 767px) {
    height: 320px;
  }
  &--has-grid {
    margin-top: 40px;

    &:before,
    &:after {
      position: absolute;
      content: '';
      top: 0;
      left: 0;
      width: calc(100% + 30px);
      height: 25%;
      margin-left: -15px;
      border-top: 1px dashed #b4b4b5;
      border-bottom: 1px dashed #b4b4b5;
    }
    &:after {
      top: 50%;
    }

    .column-chart .chart--horizontal .item:before {
      position: absolute;
      z-index: -1;
      content: '';
      bottom: 0;
      left: 50%;
      width: 1px;
      height: calc(100% + 15px);
      border-right: 1px dashed #b4b4b5;
    }
  }
  .chart {
    position: relative;
    z-index: 20;
    bottom: 0;
    width: 100%;
    height: 100%;

    .title {
      position: absolute;
      z-index: 20;
      width: 260px;
      top: calc(50% - 13px);

      color: #fff;
      font-size: 14px;
      text-align: center;
      text-transform: uppercase;
      letter-spacing: 2px;

      @media (min-width: 360px) {
        font-size: 16px;
      }
      @media (min-width: 480px) {
        font-size: 18px;
      }
    }

    .item {
      position: relative;
    }
    .bar {

      .percent {
        background-color: transparent;
      }
      .item-progress {

      }
    }
    &--horizontal {
      .title {
        left: 50%;
        transform: translateX(-50%) translateY(-50%) rotate(-90deg);
      }
      .item {
        float: left;
        height: 100%;
        width: 20%;


        .bar {
          position: absolute;
          bottom: 0;
          left: 3px;
          width: 94%;
          height: 100%;
          background-color: rgba(0,0,0, .25);

          .percent {
            display: block;
            position: absolute;
            z-index: 25;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 26px;
            line-height: 26px;
            color: #fff;
            font-size: 14px;
            font-weight: 700;
            text-align: center;
            letter-spacing: 1px;
          }
          .item-progress {
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 0;
            color: #fff;
            background-color: $green;
            transition: height .2s ease-in-out;
          }
        }
      }
    }
    &--vertical {
      .title {
        width: 100%;
      }
      .item {
        width: 100%;
        height: 25%;

        .bar {
          position: absolute;
          bottom: 0;
          left: 3px;
          width: 100%;
          height: 94%;
          background-color: rgba(0,0,0, .25);

          .percent {
            display: block;
            position: absolute;
            z-index: 25;
            bottom: 0;
            left: 15px;
            height: 26px;
            line-height: 26px;
            color: #fff;
            font-size: 14px;
            font-weight: 700;
            text-align: center;
            letter-spacing: 1px;
          }
          .item-progress {
            position: absolute;
            left: 0;
            width: 100%;
            height: 100%;
            color: #fff;
            background-color: $green;
            transition: height .2s ease-in-out;
          }
        }
      }
    }
  }
  &--results {
    .chart {
      .item {
      }
      .bar {
        .percent {
        }
        .item-progress {
          background-color: $blue !important;
        }
      }
    }
  }
}
.priceBox {
  position: absolute;
  z-index: 0;
  top: 8px;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0,0,0, .15);

  .marginBox {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 50%;
    background-color: rgba($yellow, .5);
    color: #fff;
    min-height: 1px;

    &__budget {
      position: absolute;
      bottom: 0;
      left: 0;
      right: -100%;
      background-color: rgba($orange, .5);
    }

    &__label {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 5px;
      font-size: 14px;
    }
  }
  .avgBox {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 0;
    margin-top: -1px;
    border-bottom: 2px dashed rgba(0,0,0, .5);
    color: rgba(0,0,0, .5);

    &__label {
      position: absolute;
      bottom: 100%;
      left: 0;
      right: 0;
      padding: 5px;
      font-size: 11px;
    }
  }
  .marginalBox {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    height: 1px;
    background: #000;

    &__label {
      position: absolute;
      left: 0;
      right: 0;
      top: 5px;
      font-size: 12px;
      text-align: center;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue